import styles from "../uploadedFiles/uploadedFiles.module.scss";
import { useSelector } from "redux/hooks";
import { IFileItem } from "redux/actions";
import {
  CSVIcon,
  EMLIcon,
  PDFIcon,
  TextIcon,
  PPTIcon,
  XLSXIcon,
  XLSIcon,
  SRTIcon,
  PPTXIcon,
  DocIcon,
} from "pages/ChatPage/components/icons/DocIcon";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import classNames from "classnames";
import {
  getRelativeTime,
  textExtensions,
  audioURL,
  videoURL,
  imgURL,
} from "utils/constants";
import { DefaultIcon } from "../icons/LinkIcon";
import { EThemeType } from "redux/reducers";
import FilePreviewModal from "../FilePreviewModal/FilePreviewModal";
import { adaptFileContent } from "utils/functions";
import {
  formatFileSize,
  //   getProcessedFileName,
  isImageFile,
} from "utils/fileProcessing";
import { AudioPreview } from "pages/ChatPage/components/searchField/AudioPreview";
import VideoPreview from "pages/ChatPage/components/searchField/VideoPreview";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import React from "react";
import {
  ImgDownload,
  ZoomIn,
  ZoomOut,
} from "pages/ChatPage/pages/chatHistory/components/icons/ImgUserControlIcons";

const renderFileIcon = (
  fileExtension: string,
  theme: EThemeType | undefined
) => {
  const fileExt = fileExtension.split(".").pop()?.toLowerCase(); // Extract file extension

  if (fileExt && (textExtensions.includes(fileExt) || videoURL.includes(fileExt) || audioURL.includes(fileExt) )) {
    return (
      <div
        className={classNames(styles.loader, {
          [styles.light]: theme === "light",
          [styles.dark]: theme === "dark",
        })}
      >
        <span className="text-[8px] font-medium text-[#A09FA2] uppercase">
          {fileExt}
        </span>
      </div>
    );
  }

  switch (fileExtension) {
    case "pdf":
      return <PDFIcon />;
    case "csv":
      return <CSVIcon />;
    case "txt":
      return <TextIcon />;
    case "xlsx":
      return <XLSXIcon />;
    case "xls":
      return <XLSIcon />;
    case "srt":
      return <SRTIcon />;
    case "eml":
      return <EMLIcon />;
    case "pptx":
      return <PPTXIcon />;
    case "ppt":
      return <PPTIcon />;
    case "docx":
      return <DocIcon />;
    // case "wmv":
    //   return "wmv";
    // case "mp4":
    //   return "mp4";
    // case "mpeg":
    //   return "mpeg";
    // case "mpg":
    //   return "mpg";
    // case "webm":
    //   return "webm";
    // case "3gpp":
    //   return "3gpp";
    // case "mp3":
    //   return "mp3";
    // case "flac":
    //   return "flac";
    // case "wav":
    //   return "wav";
    default:
      if (process.env.REACT_APP_ENVIRONMENT !== "production") {
        console.warn(`Unknown file extension encountered: "${fileExtension}"`);
      }
      return <DefaultIcon />;
  }
};

// Moved outside component to prevent recreation on each render
const getFormattedFileSize = (file: IFileItem): string => {
  if (file?.size === undefined || file?.size === null) {
    return "Unknown size";
  }

  const fileSize = Number(file.size);
  const size = formatFileSize(fileSize);

  return size;
};
interface IProps {
  chatInfo?: boolean;
}
export const ChatContentFiles = ({ chatInfo }: IProps) => {
  const { theme } = useSelector((state) => state.authReducer);
  const { FileContent } = useSelector((state) => state.chatReducer);
  const [previewFile, setPreviewFile] = useState<IFileItem | null>(null);

  const [audioPreview, setAudioPreview] = useState<{
    path: string;
    name: string;
  } | null>(null);

  // Video preview states
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);
  const [showVideoPreview, setShowVideoPreview] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    path: string;
    name: string;
  } | null>(null)

  // Helper function to clean up filename
const cleanupFileName = (fileName: string) => {
    if (!fileName) return "Unnamed file";
    
    try {
      // First decode URL encoded characters
      const decodedName = decodeURIComponent(fileName);
      
      // Check if it has the website-timestamp- pattern and remove it
      const websiteTimestampPattern = /^website-\d+-/;
      if (websiteTimestampPattern.test(decodedName)) {
        return decodedName.replace(websiteTimestampPattern, '');
      }
      
      return decodedName;
    } catch (e) {
      // If decoding fails, try to clean up without decoding
      const websiteTimestampPattern = /^website-\d+-/;
      if (websiteTimestampPattern.test(fileName)) {
        return fileName.replace(websiteTimestampPattern, '');
      }
      return fileName;
    }
  };

  // Process files only once when wsUploadedFiles changes
  const processedFiles = useMemo(() => {
    if (!FileContent || FileContent.length === 0) return [];

    return FileContent.map((file: IFileItem) => {
      let extension = file.mime_type?.toLowerCase() || "";

      if (!extension) {
        extension = file.path?.split(".").pop()?.toLowerCase() || "";
      }

      return {
        ...file,
        processedFileName: cleanupFileName(file.name),
        formattedFileSize: getFormattedFileSize(file),
        fileIcon: file ? renderFileIcon(extension, theme) : <DefaultIcon />,
        Filepath: file.path,
        timeInfo:
          file.created_at === file.updated_at
            ? ` Added ${getRelativeTime(file.created_at)}`
            : ` Updated ${getRelativeTime(file.updated_at)}`,
      };
    });
  }, [FileContent]);

  // Add function to handle file preview
  const handlePreviewFile = useCallback(
    (file: (typeof processedFiles)[0], e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!file) return;

      if (audioURL.includes(file.mime_type ?? "")) {
        setAudioPreview({
          path: file.path || "",
          name: file.processedFileName,
        });
      } else if (videoURL.includes(file.mime_type ?? "")) {
        setSelectedVideo({
          path: file.path || "",
          name: file.processedFileName,
        });
        setShowVideoPreview(true);
        setIsVideoLoading(true);
      } else {
        // Create a new object with the correct fileType property
        const previewFileWithType = {
          ...file,
          fileType: "document" as const, // Explicitly set fileType to "document"
        };

        setPreviewFile(previewFileWithType);
      }
    },
    []
  );

  // Add function to close preview
  const closePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  const handleVideoLoad = useCallback(() => {
    setIsVideoLoading(false);
  }, []);

  const closeVideoPreview = useCallback(() => {
    setShowVideoPreview(false);
    setSelectedVideo(null);
  }, []);

  const closeAudioPreview = useCallback(() => {
    setAudioPreview(null);
  }, []);

  const [maxHeight, setMaxHeight] = useState("auto");
  const containerRef = useRef<HTMLDivElement | null>(null); // ✅ Explicit type

  useEffect(() => {
    const updateMaxHeight = () => {
      if (containerRef.current) {
        const parent = containerRef.current.parentElement;
        if (parent) {
          const availableHeight =
            window.innerHeight -
            containerRef.current.getBoundingClientRect().top -
            43; // Adjust for padding
          setMaxHeight(`${Math.max(availableHeight, 150)}px`); // Ensure minimum height of 150px
        }
      }
    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [FileContent]);

  return (
    <>
      {previewFile && (
        <FilePreviewModal
          file={adaptFileContent(previewFile)}
          onClose={closePreview}
          getsize={true}
        />
      )}

      {showVideoPreview && selectedVideo && (
        <VideoPreview
          videos={[selectedVideo]}
          loading={isVideoLoading}
          handleVideoLoad={handleVideoLoad}
          onClose={closeVideoPreview}
        />
      )}

      {/* Audio preview modal */}
      {audioPreview && (
        <AudioPreview audio={audioPreview} onClose={closeAudioPreview} />
      )}
      {/* Wrap entire file list with PhotoProvider for image previews */}
      <PhotoProvider
        maskOpacity={0.91}
        speed={() => 800}
        easing={(type) =>
          type === 2
            ? "cubic-bezier(0.36, 0, 0.66, -0.56)"
            : "cubic-bezier(0.34, 1.56, 0.64, 1)"
        }
        toolbarRender={({ onScale, scale, index, images }) => {
          // Check if the current image is a GIF
          const currentSrc = images[index]?.src || "";
          const isGif = currentSrc.toLowerCase().endsWith(".gif");

          // Don't render toolbar controls for GIFs
          if (isGif) {
            return null;
          }
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                gap: "12px",
                marginTop: "12px",
                userSelect: "none",
              }}
            >
              {/* Zoom out button */}
              <div
                onClick={() => onScale(scale - 0.5)}
                style={{ cursor: "pointer", padding: "8px" }}
              >
                <ZoomOut />
              </div>

              {/* Zoom in button */}
              <div
                onClick={() => onScale(scale + 0.5)}
                style={{ cursor: "pointer", padding: "8px" }}
              >
                <ZoomIn />
              </div>

              {/* Download button */}
              <div
                onClick={() => {
                  // Create a custom event to trigger the download
                  const event = new CustomEvent("photo-view-download-image");
                  document.dispatchEvent(event);
                }}
                style={{ cursor: "pointer", padding: "8px" }}
              >
                <ImgDownload />
              </div>
            </div>
          );
        }}
      >
        <div
          ref={containerRef}
          className={classNames(styles.addSources, {
            [styles.chatInfo]: chatInfo,
          })}
          style={{
            maxHeight,
            position: "relative",
            overflowX: "hidden",
          }}
        >
          {processedFiles.map((file) => {
            const extension = file.Filepath.split(".").pop()?.toLowerCase() || "";
            const isImage = isImageFile(file.mime_type || "") || imgURL.includes(extension);
  // Use the Filepath that was properly set in processedFiles
  const filePath = file.Filepath || file.path;
            const fileItem = (
              <div
                className={classNames(styles.filesContainer, {
                  [styles.light]: theme === "light",
                  [styles.dark]: theme === "dark",
                })}
                onClick={(e) => {
                  if (!isImage) handlePreviewFile(file, e);
                }}
                style={{ cursor: "pointer" }}
              >
                <div className={styles.fileWrapper}>
                  {isImage ? (
                    <div
                    className="w-[30px] h-[30px] overflow-hidden rounded flex flex-row items-center justify-center flex-shrink-0"
                      // style={{
                      //   width: "30px",
                      //   height: "30px",
                      //   overflow: "hidden",
                      //   borderRadius: "4px",
                      // }}
                    >
                      <img
                        src={filePath} // Use file.path directly as the image source
                        alt="Uploaded Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                            console.error(`Failed to load image: ${filePath}`);
                            // Replace the img with the file icon
                            e.currentTarget.style.display = 'none';
                            // Create a span to hold the icon
                            const iconSpan = document.createElement('span');
                            iconSpan.innerHTML = '📄'; // Simple fallback icon
                            // e.currentTarget.parentElement.appendChild(iconSpan);
                          }}
                      />    
                    </div>
                  ) : (
                    <div>{file.fileIcon}</div>
                  )}
                  <div className={styles.fileNameWrapper}>
                    <div
                      className={classNames(styles.fileName, {
                        [styles.light]: theme === "light",
                        [styles.dark]: theme === "dark",
                        [styles.chatInfo]: chatInfo,
                      })}
                    >
                      {file.processedFileName}
                    </div>

                    <span className="text-[12px] font-normal leading-[15px] text-[#737373] dark: text-[rgba(255,255,255,0.7)">
                      {file.formattedFileSize}
                      <span className="mx-[4px]">•</span>
                      {file.timeInfo}
                    </span>
                  </div>
                </div>
              </div>
            );

            if (isImage) {
              return (
                <PhotoView key={file.id} src={file.path}>
                  {fileItem}
                </PhotoView>
              );
            }

            return <React.Fragment key={file.id}>{fileItem}</React.Fragment>;
          })}
        </div>
      </PhotoProvider>
    </>
  );
};
